import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Mountain, Snowflake, Wind, Thermometer, AlertTriangle,
  ChevronUp, ChevronDown, Map, Timer
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkiReport({ lat, lon, isDarkMode, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSkiData();
  }, [lat, lon]);

  const fetchSkiData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/ski`, {
        params: { lat, lon }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch ski data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'space-y-3' : 'glass rounded-2xl p-6'} animate-pulse`} data-testid={compact ? 'ski-report-compact-loading' : 'ski-report-loading'}>
        <div className="h-6 bg-white/10 rounded w-48"></div>
        <div className={compact ? 'h-20 bg-white/10 rounded-xl' : 'h-48 bg-white/10 rounded'}></div>
        {compact && <div className="grid grid-cols-4 gap-2"><div className="h-12 bg-white/10 rounded-xl" /><div className="h-12 bg-white/10 rounded-xl" /><div className="h-12 bg-white/10 rounded-xl" /><div className="h-12 bg-white/10 rounded-xl" /></div>}
      </div>
    );
  }

  if (!data?.success) {
    return null;
  }

  const { snow, weather, operations, terrain, conditions_rating, elevation, avalanche_danger, is_ski_season } = data;

  const getConditionColor = (label) => {
    if (label === "Epic" || label === "Excellent") return "text-green-400";
    if (label === "Very Good" || label === "Good") return "text-emerald-400";
    if (label === "Fair") return "text-yellow-400";
    return "text-red-400";
  };

  const getAvalancheColor = (danger) => {
    if (danger === "Low") return "text-green-400";
    if (danger === "Moderate") return "text-yellow-400";
    if (danger === "Considerable") return "text-orange-400";
    return "text-red-400";
  };

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
        data-testid="ski-report-widget-compact"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
              <Mountain className={`w-5 h-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold">Ski & Snow Report</h3>
              <p className="text-xs opacity-60 truncate">{data.resort}</p>
            </div>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${operations.status === 'Open' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`} data-testid="ski-compact-status">
            {operations.status}
          </div>
        </div>

        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20' : 'bg-gradient-to-r from-cyan-100 to-blue-100'}`} data-testid="ski-compact-conditions">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs opacity-60">Today</p>
              <p className={`text-xl font-black ${getConditionColor(conditions_rating.label)}`}>{conditions_rating.label}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black">{conditions_rating.index}/10</p>
              <p className="text-xs opacity-60 truncate max-w-[180px]">{conditions_rating.recommendation}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2" data-testid="ski-compact-snow-stats">
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">Base</p>
            <p className="font-bold">{snow.base_depth_inches}&quot;</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${snow.new_24h_inches > 0 ? isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100' : isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">24h</p>
            <p className="font-bold text-cyan-400">{snow.new_24h_inches > 0 ? `+${snow.new_24h_inches}"` : '0"'}</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">Lifts</p>
            <p className="font-bold">{operations.lifts_open}/{operations.lifts_total}</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">Trails</p>
            <p className="font-bold">{operations.trails_open}/{operations.trails_total}</p>
          </div>
        </div>

        <div className={`grid grid-cols-2 gap-2 text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} data-testid="ski-compact-weather-row">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>{weather.temperature_f}°F · {weather.wind_mph} mph</div>
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>{snow.snow_type} · {snow.surface_conditions}</div>
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="ski-compact-extra-snow-stats">
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">48h</p>
            <p className="font-bold">{snow.new_48h_inches}&quot;</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">7d</p>
            <p className="font-bold">{snow.new_7d_inches}&quot;</p>
          </div>
          <div className={`p-2 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-[11px] opacity-60">Parks</p>
            <p className="font-bold">{operations.terrain_parks_open}</p>
          </div>
        </div>

        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="ski-compact-terrain-breakdown">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold">Terrain mix</p>
            <p className="text-[11px] opacity-60">Base {elevation.base_ft} ft · Summit {elevation.summit_ft} ft</p>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden mb-2">
            <div className="bg-green-500" style={{ width: `${terrain.beginner_percent}%` }} />
            <div className="bg-blue-500" style={{ width: `${terrain.intermediate_percent}%` }} />
            <div className="bg-black" style={{ width: `${terrain.advanced_percent}%` }} />
            <div className="bg-red-500" style={{ width: `${terrain.expert_percent}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-1 text-[10px] opacity-70">
            <span>Beg {terrain.beginner_percent}%</span><span>Int {terrain.intermediate_percent}%</span><span>Adv {terrain.advanced_percent}%</span><span>Exp {terrain.expert_percent}%</span>
          </div>
        </div>

        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'}`} data-testid="ski-compact-safety-note">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-cyan-500">Mountain read</p>
            <p className={`text-xs font-bold ${getAvalancheColor(avalanche_danger)}`}>Avalanche: {avalanche_danger}</p>
          </div>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{weather.visibility} visibility · {conditions_rating.recommendation}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
            <Mountain className={`w-5 h-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
          </div>
          <div>
            <h3 className="font-bold">Ski & Snow Report</h3>
            <p className="text-sm opacity-60">{data.resort}</p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          operations.status === 'Open' 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {operations.status}
        </div>
      </div>

      {/* Season Warning */}
      {!is_ski_season && (
        <div className={`mb-4 p-3 rounded-xl ${isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50'}`}>
          <div className="flex items-center gap-2 text-yellow-500">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Off-season - Limited operations</span>
          </div>
        </div>
      )}

      {/* Conditions Rating */}
      <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20' : 'bg-gradient-to-r from-cyan-100 to-blue-100'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-60 mb-1">Today&apos;s Conditions</p>
            <p className={`text-2xl font-bold ${getConditionColor(conditions_rating.label)}`}>
              {conditions_rating.label}
            </p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-black">{conditions_rating.index}/10</p>
            <p className="text-sm opacity-60">{conditions_rating.recommendation}</p>
          </div>
        </div>
      </div>

      {/* Snow Report */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Snowflake className="w-5 h-5 mx-auto mb-1 text-cyan-400" />
          <p className="text-xs opacity-60">Base Depth</p>
          <p className="text-xl font-bold">{snow.base_depth_inches}&quot;</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${
          snow.new_24h_inches > 0 
            ? isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'
            : isDarkMode ? 'bg-white/5' : 'bg-black/5'
        }`}>
          <p className="text-xs opacity-60">New (24h)</p>
          <p className="text-xl font-bold text-cyan-400">
            {snow.new_24h_inches > 0 ? `+${snow.new_24h_inches}"` : '0"'}
          </p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <p className="text-xs opacity-60">New (48h)</p>
          <p className="text-xl font-bold">{snow.new_48h_inches}&quot;</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <p className="text-xs opacity-60">New (7d)</p>
          <p className="text-xl font-bold">{snow.new_7d_inches}&quot;</p>
        </div>
      </div>

      {/* Snow Type & Weather */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <h4 className="text-sm font-semibold mb-3">Snow Conditions</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Snow Type</span>
              <span className="font-medium">{snow.snow_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Surface</span>
              <span className="font-medium">{snow.surface_conditions}</span>
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <h4 className="text-sm font-semibold mb-3">Mountain Weather</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm opacity-70 flex items-center gap-1">
                <Thermometer className="w-3 h-3" /> Temp
              </span>
              <span className="font-medium">{weather.temperature_f}°F</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm opacity-70 flex items-center gap-1">
                <Wind className="w-3 h-3" /> Wind
              </span>
              <span className="font-medium">{weather.wind_mph} mph</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm opacity-70">Visibility</span>
              <span className="font-medium">{weather.visibility}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Operations */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <ChevronUp className="w-5 h-5 mx-auto mb-1 opacity-60" />
          <p className="text-xs opacity-60">Lifts</p>
          <p className="font-bold">{operations.lifts_open}/{operations.lifts_total}</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Map className="w-5 h-5 mx-auto mb-1 opacity-60" />
          <p className="text-xs opacity-60">Trails</p>
          <p className="font-bold">{operations.trails_open}/{operations.trails_total}</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <ChevronDown className="w-5 h-5 mx-auto mb-1 opacity-60" />
          <p className="text-xs opacity-60">Terrain Parks</p>
          <p className="font-bold">{operations.terrain_parks_open}</p>
        </div>
      </div>

      {/* Terrain Breakdown */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold mb-2">Terrain Breakdown</h4>
        <div className="flex h-3 rounded-full overflow-hidden">
          <div 
            className="bg-green-500" 
            style={{ width: `${terrain.beginner_percent}%` }}
            title={`Beginner: ${terrain.beginner_percent}%`}
          />
          <div 
            className="bg-blue-500" 
            style={{ width: `${terrain.intermediate_percent}%` }}
            title={`Intermediate: ${terrain.intermediate_percent}%`}
          />
          <div 
            className="bg-black" 
            style={{ width: `${terrain.advanced_percent}%` }}
            title={`Advanced: ${terrain.advanced_percent}%`}
          />
        </div>
        <div className="flex justify-between text-xs mt-1 opacity-60">
          <span>🟢 {terrain.beginner_percent}%</span>
          <span>🔵 {terrain.intermediate_percent}%</span>
          <span>⚫ {terrain.advanced_percent}%</span>
        </div>
        <p className="text-xs opacity-50 mt-1">{terrain.acres} skiable acres</p>
      </div>

      {/* Elevation & Avalanche */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <p className="text-xs opacity-60 mb-1">Elevation</p>
          <p className="text-sm">
            Base: {elevation.base}&apos; • Summit: {elevation.summit}&apos; • Drop: {elevation.vertical_drop}&apos;
          </p>
        </div>
        
        {is_ski_season && avalanche_danger !== "N/A" && (
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <p className="text-xs opacity-60 mb-1">Avalanche Danger</p>
            <p className={`text-sm font-medium ${getAvalancheColor(avalanche_danger)}`}>
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {avalanche_danger}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
